'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Slide, MediaType, View } from '@/shared/types';
import { MediaDisplay } from './ui/mediaDisplay';
import { CaretLeftIcon, CaretRightIcon, ArrowsOutSimpleIcon, ArrowsInSimpleIcon, ArrowsOutIcon, ArrowsInIcon, Image as ImageIcon, Video as VideoIcon, Microphone as MicrophoneIcon, FilePdf as FilePdfIcon, Link as LinkIcon, File as FileIcon, Minus, Plus, TextAaIcon, ArrowsLeftRightIcon, ArrowsDownUpIcon } from '@phosphor-icons/react';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Tooltip } from '@heroui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/popover';
import { Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import ReactMarkdown from 'react-markdown';
dayjs.extend(utc);
dayjs.extend(timezone);

// Constants
const TITLE_SLIDE_ID = 'TITLE_SLIDE' as const;
const DEFAULT_TITLE = 'Timeline';

// Font size constants
const FONT_SIZE_BASE = {
  title: 3,
  // 3rem (48px) - equivalent to text-5xl
  body: 1.875,
  // 1.875rem (30px) - equivalent to text-3xl
  date: 1.875,
  // 1.875rem (30px) - equivalent to text-3xl
  eventNumber: 1.25 // 1.25rem (20px) - equivalent to text-xl
} as const;
const FONT_SIZE_INCREMENT = 0.25;
const FONT_SIZE_SCALE_DEFAULT = 0;

// Icon size constants
const ICON_SIZE_DEFAULT = 24;
const ICON_SIZE_SMALL = 20;

// Modal size constant
const MODAL_SIZE = '5xl';

// Animation constants
const HOVER_SCALE = 1.05;
const SPRING_STIFFNESS = 300;
const SPRING_DAMPING = 20;

// Color constants
const POPOVER_BG_COLOR = '#2C3A4A';
const SHADOW_COLOR = 'rgba(56, 189, 248, 0.15)';

// Title slide default values
const TITLE_SLIDE_DISPLAY_ORDER = 0;
const TITLE_SLIDE_INDEX = 0;
const TITLE_SLIDE_DATE_GROUP_INDEX = 0;
const TITLE_SLIDE_TOTAL_EVENTS = 1;

// Convert S3 URL to proxy URL for display
const convertS3UrlToProxyUrl = (url: string | undefined): string => {
  if (!url) return '';
  if (url.startsWith('s3://')) {
    return `/api/proxy/${encodeURIComponent(url)}`;
  }
  return url; // Return as-is for http/https URLs
};
interface TPCourtroomProps {
  title?: string;
  description?: string;
  media?: string;
  allSlides: Slide[];
}
interface ProcessedSlide extends Slide {
  slideIndex: number;
  dateGroupIndex: number;
  totalEventsOnDate: number;
  displayDate: string;
}
const TPCourtroom: React.FC<TPCourtroomProps> = ({
  title,
  description,
  media,
  allSlides
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [fontSizeScale, setFontSizeScale] = useState<number>(FONT_SIZE_SCALE_DEFAULT);
  const [isMediaFullscreen, setIsMediaFullscreen] = useState(false);
  const [fontSizeDropdownOpen, setFontSizeDropdownOpen] = useState(false);
  const [isEventGridModalOpen, setIsEventGridModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true); // Default to fullscreen for courtroom view
  const [isHorizontalLayout, setIsHorizontalLayout] = useState(true); // Default to horizontal layout on desktop

  // ========== Slide Processing ==========

  // Process slides: flatten and add metadata
  const processedSlides = React.useMemo(() => {
    const slides: ProcessedSlide[] = [];

    // Add title slide if present
    if (title || description || media) {
      slides.push({
        id: TITLE_SLIDE_ID,
        displayOrder: TITLE_SLIDE_DISPLAY_ORDER,
        slideTitle: title || DEFAULT_TITLE,
        slideDescription: description,
        media: media ? [{
          mediaSrc: media,
          mediaType: 'image' as MediaType
        }] : undefined,
        show: true,
        slideIndex: TITLE_SLIDE_INDEX,
        dateGroupIndex: TITLE_SLIDE_DATE_GROUP_INDEX,
        totalEventsOnDate: TITLE_SLIDE_TOTAL_EVENTS,
        displayDate: ''
      });
    }

    // Group events by date for numbering
    const eventsByDate = new Map<string, Slide[]>();
    allSlides.forEach(slide => {
      if (slide.show && slide.date) {
        const dateKey = slide.date;
        if (!eventsByDate.has(dateKey)) {
          eventsByDate.set(dateKey, []);
        }
        eventsByDate.get(dateKey)!.push(slide);
      }
    });

    // Process events and add metadata
    let slideIndex = slides.length;
    eventsByDate.forEach((events, dateKey) => {
      events.forEach((event, eventIndex) => {
        slides.push({
          ...event,
          slideIndex: slideIndex++,
          dateGroupIndex: eventIndex,
          totalEventsOnDate: events.length,
          displayDate: event.displayDate || event.date || dateKey
        });
      });
    });
    return slides;
  }, [allSlides, title, description, media]);
  const currentSlide = processedSlides[currentSlideIndex];
  const totalSlides = processedSlides.length;

  // ========== Date Utilities ==========

  // Format date for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = dayjs(dateStr);
      return date.format('MMMM DD, YYYY');
    } catch {
      return dateStr;
    }
  };

  // Get event number text
  const getEventNumberText = (slide: ProcessedSlide): string => {
    if (slide.id === TITLE_SLIDE_ID) return '';
    return `${slide.dateGroupIndex + 1} of ${slide.totalEventsOnDate}`;
  };

  // Calculate date range and total events for title slide
  const {
    dateRange,
    totalEvents
  } = React.useMemo(() => {
    const eventSlides = allSlides.filter(slide => slide.show && slide.date);
    const totalEvents = eventSlides.length;
    if (totalEvents === 0) {
      return {
        dateRange: '',
        totalEvents: 0
      };
    }
    const dates = eventSlides.map(slide => slide.date).filter((date): date is string => !!date).map(date => dayjs(date));
    if (dates.length === 0) {
      return {
        dateRange: '',
        totalEvents: 0
      };
    }

    // Find min and max dates
    let minDate = dates[0];
    let maxDate = dates[0];
    dates.forEach(date => {
      if (date.isBefore(minDate)) {
        minDate = date;
      }
      if (date.isAfter(maxDate)) {
        maxDate = date;
      }
    });

    // Format date range
    let dateRangeStr = '';
    if (minDate.isSame(maxDate, 'day')) {
      // Same day
      dateRangeStr = minDate.format('MMMM D, YYYY');
    } else if (minDate.isSame(maxDate, 'month') && minDate.isSame(maxDate, 'year')) {
      // Same month and year
      dateRangeStr = `${minDate.format('MMMM D')} – ${maxDate.format('D, YYYY')}`;
    } else if (minDate.isSame(maxDate, 'year')) {
      // Same year, different months
      dateRangeStr = `${minDate.format('MMMM D')} – ${maxDate.format('MMMM D, YYYY')}`;
    } else {
      // Different years
      dateRangeStr = `${minDate.format('MMMM D, YYYY')} – ${maxDate.format('MMMM D, YYYY')}`;
    }
    return {
      dateRange: dateRangeStr,
      totalEvents
    };
  }, [allSlides]);

  // ========== Font Size Utilities ==========

  // Calculate font sizes based on scale (rem-based)
  const getFontSizes = (scale: number) => {
    return {
      title: `${FONT_SIZE_BASE.title + scale * FONT_SIZE_INCREMENT}rem`,
      body: `${FONT_SIZE_BASE.body + scale * FONT_SIZE_INCREMENT}rem`,
      date: `${FONT_SIZE_BASE.date + scale * FONT_SIZE_INCREMENT}rem`,
      eventNumber: `${FONT_SIZE_BASE.eventNumber + scale * FONT_SIZE_INCREMENT}rem`
    };
  };
  const currentFontSizes = useMemo(() => getFontSizes(fontSizeScale), [fontSizeScale]);

  // ========== Media Utilities ==========

  // Get media type icon
  const getMediaTypeIcon = (slide: ProcessedSlide) => {
    const mediaType = slide.mediaType || slide.media?.[0]?.mediaType;
    switch (mediaType) {
      case 'image':
        return <ImageIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
      case 'video':
        return <VideoIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
      case 'audio':
        return <MicrophoneIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
      case 'pdf':
        return <FilePdfIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
      case 'youtube':
        return <VideoIcon size={ICON_SIZE_DEFAULT} className="text-red-600" />;
      case 'url':
        return <LinkIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
      default:
        return <FileIcon size={ICON_SIZE_DEFAULT} className="text-gray-600" />;
    }
  };

  // ========== Navigation Utilities ==========

  // Navigation functions
  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlideIndex(index);
    }
  }, [totalSlides]);
  const goToNextSlide = useCallback(() => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  }, [currentSlideIndex, totalSlides]);
  const goToPreviousSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  }, [currentSlideIndex]);

  // Handle event card click
  const handleEventCardClick = (index: number) => {
    goToSlide(index);
    setIsEventGridModalOpen(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Prevent font size shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        goToPreviousSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      } else if (e.key === '+' || e.key === '=') {
        // Plus key or equals key (shifted plus)
        e.preventDefault();
        setFontSizeScale(prev => prev + 1);
      } else if (e.key === '-' || e.key === '_') {
        // Minus key or underscore (shifted minus)
        e.preventDefault();
        setFontSizeScale(prev => prev - 1);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToNextSlide, goToPreviousSlide]);

  // Swipe gestures
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goToNextSlide,
    onSwipedRight: goToPreviousSlide,
    trackMouse: true
  });

  // ========== Effects ==========

  // Handle fullscreen toggle
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('courtroom-fullscreen');
    } else {
      document.body.classList.remove('courtroom-fullscreen');
    }
    return () => {
      document.body.classList.remove('courtroom-fullscreen');
    };
  }, [isFullscreen]);
  if (!currentSlide) {
    return <div className="flex items-center justify-center h-full bg-transparent text-white">
        <div className="text-2xl">No events available</div>
      </div>;
  }
  return <div className={`flex flex-col h-full bg-transparent text-white overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50' : ''}`} {...swipeHandlers} data-sentry-component="TPCourtroom" data-sentry-source-file="tp-courtroom.tsx">

      {/* Main content area */}
      <div className={`flex-1 flex flex-col min-h-0 ${isFullscreen ? 'p-8 pb-24' : 'p-8 pb-24'}`}>
        {/* Date and Event Number */}
        <div className="mb-6 flex items-baseline gap-4 justify-between flex-shrink-0">
          <div className="flex items-baseline gap-4">
            {currentSlide.id === TITLE_SLIDE_ID ? <>
                {dateRange && <div className="font-bold" style={{
              fontSize: currentFontSizes.date
            }}>
                    {dateRange}
                  </div>}
                {totalEvents > 0 && <div className="font-bold text-white/70" style={{
              fontSize: currentFontSizes.eventNumber
            }}>
                    {totalEvents} {totalEvents === 1 ? 'Event' : 'Events'}
                  </div>}
              </> : <>
                {currentSlide.displayDate && <div className="font-bold" style={{
              fontSize: currentFontSizes.date
            }}>
                    {formatDate(currentSlide.displayDate)}
                  </div>}
                {getEventNumberText(currentSlide) && <div className="font-bold text-white/70" style={{
              fontSize: currentFontSizes.eventNumber
            }}>
                    {getEventNumberText(currentSlide)}
                  </div>}
              </>}
          </div>
          <div className="absolute right-8 top-8 flex items-top gap-2">
            {/* Layout Toggle */}
            <Tooltip content={isHorizontalLayout ? 'Switch to vertical layout' : 'Switch to horizontal layout'} placement="bottom" delay={0} classNames={{
            content: `bg-[${POPOVER_BG_COLOR}] border border-white/10 shadow-lg`
          }} data-sentry-element="Tooltip" data-sentry-source-file="tp-courtroom.tsx">
              <button onClick={() => setIsHorizontalLayout(!isHorizontalLayout)} className="hidden lg:block p-2 hover:bg-white/10 rounded-lg transition-colors" aria-label={isHorizontalLayout ? 'Switch to vertical layout' : 'Switch to horizontal layout'}>
                {isHorizontalLayout ? <ArrowsLeftRightIcon size={ICON_SIZE_DEFAULT} /> : <ArrowsDownUpIcon size={ICON_SIZE_DEFAULT} />}
              </button>
            </Tooltip>
            {/* Font Size Control */}
            <Tooltip content="Font size" placement="bottom" delay={0} isDisabled={fontSizeDropdownOpen} classNames={{
            content: `bg-[${POPOVER_BG_COLOR}] border border-white/10 shadow-lg`
          }} data-sentry-element="Tooltip" data-sentry-source-file="tp-courtroom.tsx">
              <div className="inline-flex">
                <Popover placement="bottom" isOpen={fontSizeDropdownOpen} onOpenChange={setFontSizeDropdownOpen} data-sentry-element="Popover" data-sentry-source-file="tp-courtroom.tsx">
                  <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="tp-courtroom.tsx">
                    <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" aria-label="Font size">
                      <TextAaIcon size={ICON_SIZE_DEFAULT} data-sentry-element="TextAaIcon" data-sentry-source-file="tp-courtroom.tsx" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className={`bg-[${POPOVER_BG_COLOR}] border border-white/10 shadow-lg z-50`} data-sentry-element="PopoverContent" data-sentry-source-file="tp-courtroom.tsx">
                    <div className="flex items-center p-2 gap-0">
                      <button onClick={() => setFontSizeScale(prev => prev - 1)} className="px-4 py-2 text-white hover:bg-white/10 rounded transition-colors flex items-center justify-center" aria-label="Decrease font size">
                        <Minus size={ICON_SIZE_SMALL} data-sentry-element="Minus" data-sentry-source-file="tp-courtroom.tsx" />
                      </button>
                      <div className="h-6 w-px bg-white/20 mx-1" />
                      <button onClick={() => setFontSizeScale(prev => prev + 1)} className="px-4 py-2 text-white hover:bg-white/10 rounded transition-colors flex items-center justify-center" aria-label="Increase font size">
                        <Plus size={ICON_SIZE_SMALL} data-sentry-element="Plus" data-sentry-source-file="tp-courtroom.tsx" />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </Tooltip>
            <Tooltip content={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} placement="bottom" delay={0} classNames={{
            content: `bg-[${POPOVER_BG_COLOR}] border border-white/10 shadow-lg`
          }} data-sentry-element="Tooltip" data-sentry-source-file="tp-courtroom.tsx">
              <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                {isFullscreen ? <ArrowsInIcon size={ICON_SIZE_DEFAULT} /> : <ArrowsOutIcon size={ICON_SIZE_DEFAULT} />}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Title, Media and Details - Vertically centered flex container */}
        <div className="flex-1 flex flex-col justify-center min-h-0 overflow-y-auto">
          {/* Media and Content side-by-side or left-aligned if no media */}
          {currentSlide.media && currentSlide.media.length > 0 ? <div className={`grid grid-cols-1 ${isHorizontalLayout ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-16 items-start`}>
              
              {/* Media Section */}
              <div className="flex flex-col">
                <div className="relative">
                  <div className="relative h-[50vh] w-full aspect-video bg-black/20 rounded-lg overflow-hidden">
                    <MediaDisplay mediaItem={currentSlide.media[0]} isPresentation={true} />
                  </div>

                </div>
              </div>

              {/* Title and Details Section */}
              <div className="flex flex-col gap-0">
                {/* Title */}
                {currentSlide.slideTitle && <h1 className="font-bold leading-tight m-0" style={{
              fontSize: currentFontSizes.title
            }}>
                    {currentSlide.slideTitle}
                  </h1>}

                {/* Details */}
                {currentSlide.slideDescription && <div className="leading-normal [&_p]:leading-relaxed" style={{
              fontSize: currentFontSizes.body
            }}>
                    <ReactMarkdown>{currentSlide.slideDescription}</ReactMarkdown>
                  </div>}
              </div>
            </div> : <div className="flex flex-col items-start">
              {/* Title */}
              {currentSlide.slideTitle && <h1 className="font-bold mb-8 leading-tight" style={{
            fontSize: currentFontSizes.title
          }}>
                  {currentSlide.slideTitle}
                </h1>}
              {currentSlide.slideDescription && <div className="leading-normal max-w-4xl [&_p]:leading-relaxed" style={{
            fontSize: currentFontSizes.body
          }}>
                  <ReactMarkdown>{currentSlide.slideDescription}</ReactMarkdown>
                </div>}
            </div>}
        </div>
      </div>

      {/* Bottom Pagination - Fixed to viewport */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-transparent p-4 flex items-center justify-center gap-4 z-40">
        <button onClick={goToPreviousSlide} disabled={currentSlideIndex === 0} className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          <CaretLeftIcon size={ICON_SIZE_DEFAULT} data-sentry-element="CaretLeftIcon" data-sentry-source-file="tp-courtroom.tsx" />
        </button>

        {/* Visual Pagination Dots */}
        <div className="flex items-center gap-2 px-4">
          {processedSlides.map((slide, index) => <Tooltip key={index} content={<div className="flex flex-col gap-1">
                  <div className="font-semibold">
                    {slide.id === TITLE_SLIDE_ID ? dateRange || DEFAULT_TITLE : formatDate(slide.displayDate || slide.date || '')}
                  </div>
                  {slide.id !== TITLE_SLIDE_ID && getEventNumberText(slide) && <div className="text-sm text-white/70">
                      {getEventNumberText(slide)}
                    </div>}
                  {slide.id === TITLE_SLIDE_ID && totalEvents > 0 && <div className="text-sm text-white/70">
                      {totalEvents} {totalEvents === 1 ? 'Event' : 'Events'}
                    </div>}
                </div>} placement="top" delay={0} classNames={{
          content: `bg-[${POPOVER_BG_COLOR}] border border-white/10 shadow-lg`
        }}>
              <button onClick={() => goToSlide(index)} className={`transition-all ${index === currentSlideIndex ? 'w-3 h-3 bg-white rounded-full' : 'w-2 h-2 bg-white/40 rounded-full hover:bg-white/60'}`} aria-label={`Go to event ${index + 1}`} />
            </Tooltip>)}
        </div>

        <button onClick={() => setIsEventGridModalOpen(true)} className="px-4 py-2 hover:bg-white/10 rounded-lg transition-colors">
          <span className="text-xl font-medium">
            {currentSlideIndex + 1} of {totalSlides}
          </span>
        </button>

        <button onClick={goToNextSlide} disabled={currentSlideIndex === totalSlides - 1} className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          <CaretRightIcon size={ICON_SIZE_DEFAULT} data-sentry-element="CaretRightIcon" data-sentry-source-file="tp-courtroom.tsx" />
        </button>
      </div>

      {/* Fullscreen Media Overlay */}
      {isMediaFullscreen && currentSlide.media && currentSlide.media.length > 0 && <div className="fixed inset-0 bg-black z-50 flex items-center justify-center p-8" onClick={() => setIsMediaFullscreen(false)}>
          <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
            <button onClick={() => setIsMediaFullscreen(false)} className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-colors z-10">
              <ArrowsInSimpleIcon size={ICON_SIZE_DEFAULT} />
            </button>
            <MediaDisplay mediaItem={currentSlide.media[0]} isPresentation={true} />
          </div>
        </div>}

      {/* Event Grid Modal */}
      <Modal isOpen={isEventGridModalOpen} onClose={() => setIsEventGridModalOpen(false)} size={MODAL_SIZE} scrollBehavior="inside" classNames={{
      base: 'bg-white',
      header: 'border-b border-gray-200',
      body: 'p-0'
    }} data-sentry-element="Modal" data-sentry-source-file="tp-courtroom.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="tp-courtroom.tsx">
          <ModalHeader className="flex flex-row items-center gap-2 pb-2 mb-1 sm:mb-4 border-b-1 border-solid border-gray-200" data-sentry-element="ModalHeader" data-sentry-source-file="tp-courtroom.tsx">
            <h3 className="text-2xl font-semibold text-gray-900 m-0 p-0">All Events</h3>
          </ModalHeader>
          <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="tp-courtroom.tsx">
            <div className="box-border flex flex-wrap gap-4 overflow-y-auto scrollbar-hide sm:scrollbar-default custom-scrollbar3 justify-start sm:justify-center items-start w-full sm:p-4 max-h-[70vh]">
              {processedSlides.map(slide => <motion.div key={slide.slideIndex} whileHover={{
              scale: HOVER_SCALE,
              boxShadow: `0 10px 25px ${SHADOW_COLOR}`
            }} transition={{
              type: "spring",
              stiffness: SPRING_STIFFNESS,
              damping: SPRING_DAMPING
            }} onClick={() => handleEventCardClick(slide.slideIndex)} className="flex flex-col justify-between w-full sm:w-[250px] sm:min-h-[140px] items-start p-3 text-left border-2 border-solid border-gray-200 hover:border-brand-blue-light/50 rounded-lg bg-gray-100 hover:bg-white transition-colors duration-200 cursor-pointer group">
                  <div className="flex flex-col gap-2 w-full">
                    {/* Date - Always render to maintain consistent spacing */}
                    <div className="text-sm font-semibold text-gray-700 h-10">
                      {slide.displayDate || slide.date ? formatDate(slide.displayDate || slide.date || '') : '\u00A0'}
                      <span className="text-xs text-gray-500"> &nbsp;{getEventNumberText(slide) || '\u00A0'}</span>
                    </div>

                    {/* Media Type Icon - Left aligned */}
                    <div className="flex items-center justify-start w-full h-6">
                      {slide.media && slide.media.length > 0 ? getMediaTypeIcon(slide) : <FileIcon size={ICON_SIZE_DEFAULT} className="text-gray-400" />}
                    </div>

                    {/* Event Title */}
                    <div className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-brand-blue-light transition-colors min-h-[40px]">
                      {slide.slideTitle || 'Untitled'}
                    </div>

                  </div>
                </motion.div>)}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>;
};
export default TPCourtroom;